import * as THREE from 'three';
import type { DamageType } from '../../../shared/game/combat-types.js';
import { HIT_PROFILES, hitFlinch as tuning } from '../../../content/characters/rigs/hit-flinch.js';

/** What struck: how much damage, of which kind. Absent fields mean an ordinary blow. */
export interface HitImpact { damage: number; type?: DamageType; critical?: boolean }
/** World-space motion of each physical segment, inherited from the living hit springs on death. */
export type HitMotion = ReadonlyMap<THREE.Object3D, { linear: THREE.Vector3; angular: THREE.Vector3 }>;
/** The blow a cue without details stands for: a firm slash, a third of the target's health. */
const blowOf = (impact: HitImpact | undefined, maxHp: number): HitImpact => impact ?? { damage: maxHp * .3, type: 'slashing' };

/** How hard a blow lands, 0 to 1: the share of the target's health it took, lifted a little by a critical. */
export function hitStrength(impact: HitImpact, maxHp: number) {
  return THREE.MathUtils.clamp(impact.damage / Math.max(1, maxHp) + (impact.critical ? .15 : 0), 0, 1);
}

/** Seconds a hit takes to settle: a graze is quick, a heavy blow takes longer to recover from. */
export function hitSeconds(impact: HitImpact | undefined, maxHp: number) {
  const blow = blowOf(impact, maxHp);
  return HIT_PROFILES[blow.type ?? 'slashing'].settle * (tuning.settleGraze + tuning.settleStrength * hitStrength(blow, maxHp));
}

/** Natural frequency (radians per second) of springs that settle in `seconds` at the tuned damping. */
export const hitFrequency = (seconds: number) => 4 / (tuning.damping * seconds);
/** Share of a kick's speed over the frequency that a damped spring reaches at its first peak. */
const PEAK = Math.exp(-tuning.damping / Math.sqrt(1 - tuning.damping ** 2) * Math.atan(Math.sqrt(1 - tuning.damping ** 2) / tuning.damping));

/** The kick a blow gives a body: everything `HitSprings` needs, with directions in the struck unit's own frame (+z ahead, +x right). */
export interface HitKick {
  /** Angular velocity (radians per second, a rotation vector) thrown into the trunk: it tips away from the blow and twists when struck off-center. */
  spin: { x: number; y: number; z: number };
  /** Natural frequency of the springs while this blow settles, and its settling time. */
  frequency: number; seconds: number;
  /** Extra snap of a head, freedom of limbs, and shiver (radians per second of trunk spin). */
  head: number; fling: number; tremble: number;
}

/**
 * The kick of a blow. `attacker` is the direction toward whoever struck, in the target's own frame; the body is thrown the other way, so a blow
 * from the side rolls the torso sideways instead of bending it back. Its first peak tilts the trunk by the blow's strength.
 */
export function hitKick(impact: HitImpact | undefined, maxHp: number, attacker: { x: number; z: number }): HitKick {
  const blow = blowOf(impact, maxHp), profile = HIT_PROFILES[blow.type ?? 'slashing'], strength = hitStrength(blow, maxHp);
  const length = Math.hypot(attacker.x, attacker.z), ahead = length < 1e-4;
  // Unit push direction in the body's frame.
  const px = ahead ? 0 : -attacker.x / length, pz = ahead ? -1 : -attacker.z / length;
  const seconds = hitSeconds(blow, maxHp), frequency = hitFrequency(seconds);
  const speed = (tuning.tiltGraze + tuning.tiltFull * Math.sqrt(strength)) * profile.push * frequency / PEAK;
  // Up × push: the rotation that carries the top of the body toward the push.
  return { spin: { x: pz * speed, y: px * speed * .55 * profile.twist, z: -px * speed }, frequency, seconds,
    head: profile.head, fling: profile.fling, tremble: profile.tremble * speed * .5 };
}

/** How a body keeps its feet under a stagger: the hips give with it and, past a small lean, a foot steps out to catch it. */
export interface HitBalance { hipsShift: { x: number; y: number; z: number }; leftFoot: { x: number; y: number; z: number }; rightFoot: { x: number; y: number; z: number } }
/**
 * Balance under `sway`, the trunk's lean (radians, toward +x right and +z ahead in the body's frame) moving at `rate` radians per second.
 * Hip shifts are in metres; foot offsets are shares of the legs, as the human animators place feet.
 */
export function hitBalance(sway: { x: number; z: number; rate: number }): HitBalance {
  const lean = Math.hypot(sway.x, sway.z), dx = lean > 1e-6 ? sway.x / lean : 0, dz = lean > 1e-6 ? sway.z / lean : 0;
  const reach = Math.max(0, lean - .06) * 1.6, steppingLeft = dx < -.15;
  const balance: HitBalance = { hipsShift: { x: sway.x * .35, y: -lean * .3, z: sway.z * .35 }, leftFoot: { x: 0, y: 0, z: 0 }, rightFoot: { x: 0, y: 0, z: 0 } };
  for (const [foot, stepping] of [[balance.leftFoot, steppingLeft], [balance.rightFoot, !steppingLeft]] as const) {
    foot.x = dx * reach * (stepping ? 1 : .35);
    foot.z = dz * reach * (stepping ? 1 : .35);
    // The foot lifts while it travels and sets down as the lean holds or returns.
    foot.y = reach > 0 ? Math.min(.13, Math.abs(sway.rate) * .12) * (stepping ? 1 : .15) : 0;
  }
  return balance;
}

/** Direction toward `from`, in the frame of a unit at `position` facing `facing` (radians about +y, as the animator keeps it). */
export function toLocalDirection(position: { x: number; z: number }, facing: number, from: { x: number; z: number }) {
  const dx = from.x - position.x, dz = from.z - position.z, sin = Math.sin(facing), cos = Math.cos(facing);
  // Forward is (sin, cos); right is (cos, -sin).
  return { x: dx * cos - dz * sin, z: dx * sin + dz * cos };
}
